import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { errorMessage, isApiError } from '@/api/client'
import { useAuth } from '@/auth/useAuth'
import { safeNext } from '@/auth/session'
import { Button, FormField, InlineAlert, TextInput } from '@/components/ui'
import { useDocumentTitle } from '@/lib/hooks'
import { AuthCard, authLinkClass } from './auth/AuthCard'

export default function LoginPage() {
  const { user, login, signOutReason } = useAuth()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const next = safeNext(params.get('next'))

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useDocumentTitle('Sign in')

  if (user) return <Navigate to={next} replace />

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password) {
      setError('Enter your email and password.')
      return
    }
    setPending(true)
    setError(null)
    try {
      await login(email, password)
      navigate(next, { replace: true })
    } catch (err) {
      setPending(false)
      if (isApiError(err) && err.status === 401) setError('Wrong email or password.')
      else setError(errorMessage(err, "Couldn't sign in"))
    }
  }

  return (
    <AuthCard>
      <h1 className="text-[1.75rem] font-extrabold leading-none text-ink">Sign in</h1>
      <p className="mt-2 text-[13px] text-muted">Super-admin console for every ScoreLM league.</p>

      {signOutReason === 'expired' && !error ? (
        <InlineAlert tone="warn" className="mt-5">
          Your session expired. Sign in again to continue.
        </InlineAlert>
      ) : null}
      {error ? (
        <InlineAlert tone="bad" className="mt-5">
          {error}
        </InlineAlert>
      ) : null}

      <form onSubmit={submit} noValidate className="mt-5 grid gap-4">
        <FormField label="Email">
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="email"
              autoComplete="username"
              autoFocus
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={pending}
            />
          )}
        </FormField>
        <FormField label="Password">
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={pending}
            />
          )}
        </FormField>
        <div className="-mt-2 flex justify-end">
          <Link to="/forgot-password" state={{ email: email.trim() }} className={authLinkClass}>
            Forgot password?
          </Link>
        </div>
        <Button type="submit" variant="primary" block loading={pending} className="mt-1 h-10">
          {pending ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthCard>
  )
}
